import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Header from "@/components/Header";
import StickyCTA from "@/components/StickyCTA";
import LazyChatLauncher from "@/components/LazyChatLauncher";
import ComuneRedirect from "@/components/ComuneRedirect";
import ComuneSeoRouter from "@/components/ComuneSeoRouter";

// Below-the-fold layout chrome: lazy to keep main bundle tight (LCP focus).
const Footer = lazy(() => import("@/components/Footer"));
// Toaster is below-the-fold; lazy-load to keep main bundle lean.
const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// Home is the LCP-critical route. Keep it in the main bundle so it renders
// immediately after main.js executes, avoiding a second chunk roundtrip.
import Home from "@/pages/Home";

const Servizi = lazy(() => import("@/pages/Servizi"));
const Attrezzature = lazy(() => import("@/pages/Attrezzature"));
const Galleria = lazy(() => import("@/pages/Galleria"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const Recensioni = lazy(() => import("@/pages/Recensioni"));
const Contatti = lazy(() => import("@/pages/Contatti"));
const ChiSiamo = lazy(() => import("@/pages/ChiSiamo"));
const ProvincePage = lazy(() => import("@/pages/ProvincePage"));
const RegionHub = lazy(() => import("@/pages/RegionHub"));
const Calcolatore = lazy(() => import("@/pages/Calcolatore"));
const CasiStudio = lazy(() => import("@/pages/CasiStudio"));
const CaseStudyDetail = lazy(() => import("@/pages/CaseStudyDetail"));
const AdminFotoCantieri = lazy(() => import("@/pages/AdminFotoCantieri"));
const SoluzioniPage = lazy(() => import("@/pages/SoluzioniPage"));
const LpGoogleAds = lazy(() => import("@/pages/LpGoogleAds"));
const MetodiRicercaPerdite = lazy(() => import("@/pages/MetodiRicercaPerdite"));
const MiglioreDittaPage = lazy(() => import("@/pages/MiglioreDittaPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

export function LoadingScreen() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-screen">
      <div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--brand-aqua))] border-t-transparent animate-spin" />
    </div>
  );
}

// Paths where we render a self-contained LP layout: no global Header, no
// Sticky CTA, no Footer, no Chat launcher. Keeps focus on conversion.
const STANDALONE_LP_PATHS = ["/sos-perdite-toscana"];

function isStandaloneLp(pathname) {
  return STANDALONE_LP_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function AppShell() {
  const { pathname } = useLocation();
  const standalone = isStandaloneLp(pathname);
  return (
    <div className="app-shell">
      {!standalone && <Header />}
      <main className="main-content" id="main">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/servizi" element={<Servizi />} />
            <Route path="/attrezzature" element={<Attrezzature />} />
            <Route path="/galleria" element={<Galleria />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/recensioni" element={<Recensioni />} />
            <Route path="/contatti" element={<Contatti />} />
            <Route path="/chi-siamo" element={<ChiSiamo />} />
            <Route path="/zone/:slug" element={<ProvincePage />} />
            {/* Legacy URL: 301-style redirect to the new canonical URL */}
            <Route path="/comune/:slug" element={<ComuneRedirect />} />
            <Route path="/toscana" element={<RegionHub regionSlug="toscana" />} />
            <Route path="/liguria" element={<RegionHub regionSlug="liguria" />} />
            <Route path="/umbria" element={<RegionHub regionSlug="umbria" />} />
            <Route path="/lazio" element={<RegionHub regionSlug="lazio" />} />
            <Route path="/calcolatore" element={<Calcolatore />} />
            <Route path="/casi-studio" element={<CasiStudio />} />
            <Route path="/casi-studio/:slug" element={<CaseStudyDetail />} />
            <Route path="/soluzioni/:slug" element={<SoluzioniPage />} />
            {/* Standalone landing page for Google Ads paid traffic. */}
            <Route path="/sos-perdite-toscana" element={<LpGoogleAds />} />
            <Route path="/admin/foto-cantieri" element={<AdminFotoCantieri />} />
            <Route path="/metodi-ricerca-perdite-acqua" element={<MetodiRicercaPerdite />} />
            <Route path="/migliore-ditta-ricerca-perdite-toscana" element={<MiglioreDittaPage regionSlug="toscana" />} />
            <Route path="/migliore-ditta-ricerca-perdite-umbria" element={<MiglioreDittaPage regionSlug="umbria" />} />
            {/* New SEO-friendly URL: /ricerca-perdite-acqua-{slug} (single segment).
                React Router v7 cannot do inline params, so we match the whole
                segment and parse the prefix in ComuneSeoRouter. Concrete
                routes above (e.g. /servizi) win by ranking specificity. */}
            <Route path="/:fullSlug" element={<ComuneSeoRouter />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!standalone && (
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      )}
      {!standalone && <StickyCTA />}
      {!standalone && <LazyChatLauncher />}
      <Suspense fallback={null}>
        <Toaster richColors position="top-center" />
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
